import { memo, useMemo, useState, type ReactNode } from 'react';
import styles from './MetricTable.module.css';
import { InfoTip } from './ui/InfoTip';
import { Sparkline } from './Sparkline';

/**
 * The table the pages added after Availability use.
 *
 * Availability's `DataTable` is pinned to the Figma comp — exact column widths
 * summing to 1124, a fixed row height, an image column. It is right for that
 * page and wrong for a competitor board or a purchase-order list, which have a
 * different number of columns on every page. This one takes the same visual
 * language (header fill, cell rule, delta colours, sparkline) and lets the
 * columns size themselves.
 *
 * A delta is rendered only when one was supplied. Nothing here derives a
 * comparison; if the API gave none — because the figure is a state rather than
 * a measure of the window — the cell shows a value and no more.
 */

export interface MetricColumn<R> {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  /** Hover definition, from the same field dictionary the MCP uses. */
  help?: string;
  width?: string;
  /** Cell content. Return a string, or a node for anything richer. */
  render: (row: R) => ReactNode;
  /** Optional second line under the value: a delta, a qualifier. */
  sub?: (row: R) => { text: string; tone?: 'up' | 'down' } | undefined;
  /**
   * What this column sorts on.
   *
   * `render` returns a ReactNode, which cannot be ordered — "₹13.79 L" sorts
   * before "₹2 Cr" as text. When this is absent the column falls back to the
   * raw field named by `key`, which covers most columns without any page
   * having to say so; give it explicitly when the key does not match or the
   * order should differ from the value shown.
   */
  sortValue?: (row: R) => string | number | null | undefined;
  /** Set false for a column that should not be clickable (actions, icons). */
  sortable?: boolean;
}

export interface MetricTableProps<R> {
  columns: MetricColumn<R>[];
  rows: R[];
  rowKey: (row: R) => string;
  caption: string;
  loading?: boolean;
  /** Rows to draw while loading, so the frame does not jump when data lands. */
  skeletonRows?: number;
  onRowClick?: (row: R) => void;
  isSelected?: (row: R) => boolean;
  empty?: ReactNode;
  /** Show a filter box above the table. Matches any text in the row. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Column sorted on first render, if any. */
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
}

/** Alignment class per column, looked up rather than assembled from strings. */
const ALIGN_CLASS = {
  left: styles.alignLeft,
  right: styles.alignRight,
  center: styles.alignCenter,
} as const;

/** Sort key for one cell: the column's own accessor, else the raw field. */
function valueFor<R>(col: MetricColumn<R>, row: R): string | number | null {
  if (col.sortValue) return col.sortValue(row) ?? null;
  const raw = (row as Record<string, unknown>)[col.key];
  if (typeof raw === 'number' || typeof raw === 'string') return raw;
  return null;
}

/** Every primitive on the row — what the search box matches against. */
function haystack<R>(row: R): string {
  return Object.values(row as Record<string, unknown>)
    .filter((v): v is string | number => typeof v === 'string' || typeof v === 'number')
    .join(' ')
    .toLowerCase();
}

function MetricTableImpl<R>({
  columns,
  rows,
  rowKey,
  caption,
  loading,
  skeletonRows = 6,
  onRowClick,
  isSelected,
  empty,
  searchable = false,
  searchPlaceholder = 'Search…',
  defaultSort,
}: MetricTableProps<R>) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(defaultSort ?? null);
  const [query, setQuery] = useState('');

  const view = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = q ? rows.filter((r) => haystack(r).includes(q)) : rows;
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col) {
        // Copy first: sorting in place mutates the caller's array, and these
        // rows come straight from a query cache that other views share.
        out = [...out].sort((a, b) => {
          const av = valueFor(col, a);
          const bv = valueFor(col, b);
          // Missing values sort last in BOTH directions. A null is not
          // "smallest" — it is unknown, and ranking it as the worst would be a
          // claim the data does not make.
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          const cmp =
            typeof av === 'number' && typeof bv === 'number'
              ? av - bv
              : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
          return sort.dir === 'asc' ? cmp : -cmp;
        });
      }
    }
    return out;
  }, [rows, columns, sort, query]);

  const toggleSort = (key: string): void => {
    setSort((prev) => {
      // Three states, so a reader can always get back to the order the API
      // returned — which is itself meaningful (ranked by spend, by loss).
      if (prev?.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return null;
    });
  };

  /**
   * A column is only clickable if a click would actually do something.
   *
   * `key` does not always name a field — PO's Undelivered column is keyed
   * 'value' over a row field called undelivered_value — and a header that
   * looks interactive and reorders nothing is worse than a plain one.
   */
  const canSort = (col: MetricColumn<R>): boolean => {
    if (col.sortable === false) return false;
    if (col.sortValue) return true;
    return rows.some((r) => {
      const raw = (r as Record<string, unknown>)[col.key];
      return typeof raw === 'number' || typeof raw === 'string';
    });
  };

  const showSkeleton = loading === true && rows.length === 0;

  if (!showSkeleton && rows.length === 0) {
    return <div className={styles.empty}>{empty ?? 'Nothing to show for this scope.'}</div>;
  }

  return (
    <>
    {searchable && (
      <div className={styles.tools}>
        <input
          className={styles.search}
          value={query}
          placeholder={searchPlaceholder}
          aria-label={`Search ${caption}`}
          onChange={(e) => { setQuery(e.target.value); }}
        />
        <span className={styles.count}>
          {query ? `${view.length} of ${rows.length}` : `${rows.length} rows`}
        </span>
      </div>
    )}
    <table className={styles.table}>
      <caption className={styles.srOnly}>{caption}</caption>
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.key}
              scope="col"
              className={`${ALIGN_CLASS[c.align ?? 'right']} ${canSort(c) ? styles.sortable : ''} ${sort?.key === c.key ? styles.sorted : ''}`}
              style={c.width ? { width: c.width } : undefined}
              aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              {...(canSort(c) ? { onClick: () => { toggleSort(c.key); } } : {})}
            >
              <span className={styles.headInner}>
                {c.header}
                {c.help ? <InfoTip text={c.help} /> : null}
                {sort?.key === c.key && (
                  <span className={styles.caret} aria-hidden="true">{sort.dir === 'asc' ? '▲' : '▼'}</span>
                )}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {showSkeleton
          ? Array.from({ length: skeletonRows }, (_, i) => (
              <tr key={`sk-${i}`}>
                {columns.map((c) => (
                  <td key={c.key}>
                    <span className={styles.skeleton} />
                  </td>
                ))}
              </tr>
            ))
          : view.map((row) => (
              <tr
                key={rowKey(row)}
                className={`${onRowClick ? styles.clickable : ''} ${isSelected?.(row) ? styles.selected : ''}`}
                {...(onRowClick
                  ? {
                      onClick: () => { onRowClick(row); },
                      tabIndex: 0,
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      },
                    }
                  : {})}
              >
                {columns.map((c) => {
                  const sub = c.sub?.(row);
                  const align = c.align ?? 'right';
                  return (
                    <td key={c.key} className={ALIGN_CLASS[align]}>
                      <span className={styles.cellValue}>{c.render(row)}</span>
                      {sub ? (
                        <span
                          className={`${styles.cellSub} ${sub.tone ? styles[sub.tone] : ''}`}
                        >
                          {sub.text}
                        </span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
      </tbody>
    </table>
    </>
  );
}

export const MetricTable = MetricTableImpl;

/**
 * A sparkline cell.
 *
 * Nulls are passed through as gaps rather than filled with zero: on the actual
 * sales basis the last day or two of a window have no partner figure yet, and
 * zero-filling puts a cliff at the right-hand edge of every line on the page.
 */
export const TrendCell = memo(function TrendCell({
  points,
  width = 96,
  height = 18,
}: {
  points: (number | null)[] | null | undefined;
  width?: number;
  height?: number;
}) {
  const clean = (points ?? []).filter((v): v is number => v !== null);
  if (clean.length < 2) return <span className={styles.muted}>—</span>;
  return <Sparkline points={clean} width={width} height={height} />;
});

/** A small coloured pill, for states and owners. */
export function Tag({ children, tone }: { children: ReactNode; tone?: 'neutral' | 'warn' | 'bad' | 'good' }) {
  return <span className={`${styles.tag} ${styles[`tag_${tone ?? 'neutral'}`] ?? ''}`}>{children}</span>;
}
